export type ShiftBanner = {
  /** "" = filas sin turno */
  shift: string;
  kind: "NOT_ASSIGNED" | "STILL_AVAILABLE";
  /** Operaciones de las disponibilidades de ese turno (para nombrarlo en la UI). */
  operationIds: number[];
};

type AvailabilityLike = {
  shift: string | null;
  assignedToday: boolean | null;
  svc?: { operationId?: number | null } | null;
};
type ShipmentLike = { shift: string };

const shiftRank = (shift: string): number => {
  if (shift === "") return 3;
  const idx = ["AM", "PM"].indexOf(shift);
  return idx === -1 ? 2 : idx;
};

/**
 * Qué cartel mostrar por turno en el Dashboard cuando el chofer declaró
 * disponibilidad y no tiene ruta en ese turno. Con ruta en el turno no hay
 * cartel (está la tarjeta). Una fila marcada `assignedToday` basta para
 * "no te citaron"; si ninguna lo está, "seguís disponible".
 *
 * Antes se leía `assignedToday` de la primera fila del día que devolvía el
 * back — con AM y PM era una moneda.
 */
export const shiftBanners = (
  availabilities: AvailabilityLike[],
  shipments: ShipmentLike[]
): ShiftBanner[] => {
  const shiftsWithRoute = new Set(shipments.map((s) => s.shift));
  const anyRoute = shiftsWithRoute.size > 0;

  // shift -> algún assignedToday y operaciones de sus filas
  const byShift = new Map<string, { assigned: boolean; operationIds: Set<number> }>();
  for (const a of availabilities) {
    const key = a.shift ?? "";
    const entry = byShift.get(key) ?? { assigned: false, operationIds: new Set<number>() };
    entry.assigned = entry.assigned || a.assignedToday === true;
    const operationId = a.svc?.operationId;
    if (operationId != null) entry.operationIds.add(operationId);
    byShift.set(key, entry);
  }

  const banners: ShiftBanner[] = [];
  for (const [shift, { assigned, operationIds }] of byShift) {
    const covered = shift === "" ? anyRoute : shiftsWithRoute.has(shift);
    if (covered) continue;
    banners.push({
      shift,
      kind: assigned ? "NOT_ASSIGNED" : "STILL_AVAILABLE",
      operationIds: [...operationIds],
    });
  }

  return banners.sort((a, b) => shiftRank(a.shift) - shiftRank(b.shift));
};
